/**
 * useProjectSetupHandlers - Hook for Project Setup page handlers
 *
 * Extracts handler logic from ProjectSetupPage to improve separation of concerns
 * and make handlers reusable/testable.
 *
 * Responsibilities:
 * - Save form data (onChange handlers)
 * - Automatically build calculated data when inputs are complete (for review during setup)
 * - Provide save point handler (handleSaveAndContinue) for pages to trigger building
 *
 * Usage:
 * ```tsx
 * const handlers = useProjectSetupHandlers(projectId);
 * <DateInputs onUpdate={handlers.handleDateInputsChange} />
 *
 * // At save point (Continue button):
 * const results = handlers.handleSaveAndContinue();
 * ```
 */

import { useCallback, useEffect, useRef } from 'react';

import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { convertToEngineFormat } from '~/builders/taxable-value/TaxableValueInputs';
import { useProjectStore } from '~/hooks/useProjectStore';
import { BuildService } from '~/services/build-service';
import type { DateInputsForm, DRFInputsForm, MillageInputsForm, TaxValueInputsForm } from '~/types';

// Debounce delay for expensive build operations (ms)
const BUILD_DEBOUNCE_MS = 500;

export const useProjectSetupHandlers = (projectId: string) => {
  const saveDateInputsForm = useProjectStore((s) => s.saveDateInputsForm);
  const saveTaxValueInputsForm = useProjectStore((s) => s.saveTaxValueInputsForm);
  const saveMillageInputsForm = useProjectStore((s) => s.saveMillageInputsForm);
  const saveDRFInputsForm = useProjectStore((s) => s.saveDRFInputsForm);

  // Get form inputs from store for reactive building
  const dateInputs = useProjectStore((s) => s.projects[projectId]?.dateInputs);
  const taxValueInputs = useProjectStore((s) => s.projects[projectId]?.taxValueInputs);
  const dateRecords = useProjectStore((s) => s.projects[projectId]?.dateRecords);
  const clearTaxableValueMap = useProjectStore((s) => s.clearTaxableValueMap);

  // Track previous input values to detect actual changes
  // This prevents infinite loops by only acting when inputs actually change
  const previousInputsRef = useRef<string>('');

  // Debounce timers for expensive build operations
  const dateBuildTimerRef = useRef<NodeJS.Timeout | null>(null);
  const taxBuildTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle date inputs change - save form data and trigger building if valid
  const handleDateInputsChange = useCallback(
    (values: DateInputsForm) => {
      // Save form data
      saveDateInputsForm(projectId, values);
      // Building will happen automatically via useEffect when inputs are valid
    },
    [projectId, saveDateInputsForm]
  );

  // Handle tax value inputs change - save form data and trigger building if valid
  const handleTaxValueInputsChange = useCallback(
    (values: TaxValueInputsForm) => {
      // Save form data
      saveTaxValueInputsForm(projectId, values);
      // Building will happen automatically via useEffect when inputs are valid
    },
    [projectId, saveTaxValueInputsForm]
  );

  // Auto-build DateRecords when date inputs become valid (debounced)
  useEffect(() => {
    if (!dateInputs) return;

    // Clear any pending build timer
    if (dateBuildTimerRef.current) {
      clearTimeout(dateBuildTimerRef.current);
      dateBuildTimerRef.current = null;
    }

    const validation = DateBuilder.validateInputs(dateInputs);
    if (validation.isValid) {
      // Debounce the build operation to avoid running on every keystroke
      dateBuildTimerRef.current = setTimeout(() => {
        BuildService.buildDateRecords(projectId);
        dateBuildTimerRef.current = null;
      }, BUILD_DEBOUNCE_MS);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (dateBuildTimerRef.current) {
        clearTimeout(dateBuildTimerRef.current);
        dateBuildTimerRef.current = null;
      }
    };
  }, [projectId, dateInputs]);

  // Auto-build TaxableValueMap when tax inputs become valid (requires dateRecords, debounced)
  // Also clears TaxableValueMap when inputs become invalid (immediate, not debounced)
  useEffect(() => {
    // Create a stable key from inputs to detect actual changes
    const inputsKey = taxValueInputs
      ? `${taxValueInputs.initialTaxableValue || ''}|${taxValueInputs.startYearGrowthRate || ''}|${taxValueInputs.fiveYearGrowthRate || ''}|${taxValueInputs.twentyYearGrowthRate || ''}`
      : '';
    const dateRecordsKey = dateRecords ? `dates-${dateRecords.length}` : '';

    // Skip if inputs haven't actually changed
    const currentKey = `${inputsKey}|${dateRecordsKey}`;
    if (currentKey === previousInputsRef.current) {
      return;
    }
    previousInputsRef.current = currentKey;

    // Clear any pending build timer when inputs change
    if (taxBuildTimerRef.current) {
      clearTimeout(taxBuildTimerRef.current);
      taxBuildTimerRef.current = null;
    }

    // Get current taxableValueMap from store without subscribing to it
    // This prevents infinite loops when we clear it
    const store = useProjectStore.getState();
    const currentTaxableValueMap = store.projects[projectId]?.taxableValueMap;

    if (!taxValueInputs || !dateRecords) {
      // If inputs or dateRecords are missing, clear TaxableValueMap if it exists (immediate)
      if (currentTaxableValueMap) {
        clearTaxableValueMap(projectId);
      }
      return;
    }

    try {
      const engineInputs = convertToEngineFormat(taxValueInputs);
      const validation = TaxableValueBuilder.validateInputs(engineInputs);
      const isValid = validation.isValid;

      if (isValid) {
        // Debounce the build operation to avoid running on every keystroke
        taxBuildTimerRef.current = setTimeout(() => {
          BuildService.buildTaxableValueMap(projectId);
          taxBuildTimerRef.current = null;
        }, BUILD_DEBOUNCE_MS);
      } else {
        // If validation fails, clear existing TaxableValueMap (immediate)
        if (currentTaxableValueMap) {
          clearTaxableValueMap(projectId);
        }
      }
    } catch {
      // If conversion fails, clear existing TaxableValueMap (immediate)
      if (currentTaxableValueMap) {
        clearTaxableValueMap(projectId);
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (taxBuildTimerRef.current) {
        clearTimeout(taxBuildTimerRef.current);
        taxBuildTimerRef.current = null;
      }
    };
  }, [projectId, taxValueInputs, dateRecords, clearTaxableValueMap]);

  // Handle millage inputs change - save form data only
  const handleMillageInputsChange = useCallback(
    (values: MillageInputsForm) => {
      saveMillageInputsForm(projectId, values);
      // Millage inputs don't trigger building - they're just configuration
    },
    [projectId, saveMillageInputsForm]
  );

  // Handle DRF inputs change - save form data only
  const handleDRFInputsChange = useCallback(
    (values: DRFInputsForm) => {
      saveDRFInputsForm(projectId, values);
      // DRF inputs don't trigger building - they're just configuration
    },
    [projectId, saveDRFInputsForm]
  );

  // Handle save and continue - builds all calculated data at save point
  const handleSaveAndContinue = useCallback(() => {
    // Build all calculated data at save point
    const results = BuildService.buildAllForProject(projectId);

    return results;
  }, [projectId]);

  return {
    handleDateInputsChange,
    handleTaxValueInputsChange,
    handleMillageInputsChange,
    handleDRFInputsChange,
    handleSaveAndContinue,
  };
};
